(function () {
  'use strict';

  const PRODUCT_CACHE_PREFIX = 'kate-3d-product-v1:';
  const VARIANT_CACHE_PREFIX = 'kate-3d-variant-v1:';

  if (customElements.get('backdrop-3d-preview-link')) return;

  class Backdrop3DPreviewLink extends HTMLElement {
    connectedCallback() {
      this.link = this.querySelector('[data-backdrop-3d-preview-link]');
      this.productData = this.querySelector('[data-backdrop-3d-preview-product]');
      this.productForm = document.getElementById(this.dataset.productFormId);
      if (!this.link || !this.productData) return;

      this.productForm?.addEventListener('variant:changed', this.handleVariantChanged);
      this.link.addEventListener('pointerenter', this.handlePreloadIntent, { passive: true });
      this.link.addEventListener('focus', this.handlePreloadIntent, { passive: true });
      this.link.addEventListener('touchstart', this.handlePreloadIntent, { passive: true });
      this.link.addEventListener('click', this.handleClick);

      this.updateLink(this.getCurrentVariantId());
      this.setupPreload();
    }

    disconnectedCallback() {
      this.productForm?.removeEventListener('variant:changed', this.handleVariantChanged);
      this.link?.removeEventListener('pointerenter', this.handlePreloadIntent);
      this.link?.removeEventListener('focus', this.handlePreloadIntent);
      this.link?.removeEventListener('touchstart', this.handlePreloadIntent);
      this.link?.removeEventListener('click', this.handleClick);
      this.preloadObserver?.disconnect();
    }

    handleVariantChanged = (event) => {
      const variant = event.detail?.variant;
      this.updateLink(variant?.id);
      if (this.preloadActivated) this.preloadBackdrop(variant?.id, variant);
    };

    handlePreloadIntent = () => {
      this.preloadActivated = true;
      this.preloadBackdrop(this.getCurrentVariantId());
    };

    handleClick = () => {
      const variantId = this.getCurrentVariantId();
      this.updateLink(variantId);
      this.preloadBackdrop(variantId);
    };

    setupPreload() {
      if (!('IntersectionObserver' in window)) {
        this.handlePreloadIntent();
        return;
      }

      this.preloadObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.handlePreloadIntent();
        this.preloadObserver?.disconnect();
      }, { rootMargin: '400px 0px' });
      this.preloadObserver.observe(this.closest('.product-tab-item') || this);
    }

    getCurrentVariantId() {
      return this.productForm?.querySelector('input[name="id"]')?.value
        || new URL(this.link.href, window.location.origin).searchParams.get('backdrop_variant')
        || '';
    }

    normalizeVariantGid(value) {
      const normalized = String(value || '').trim();
      if (/^\d+$/.test(normalized)) return `gid://shopify/ProductVariant/${normalized}`;
      if (/^gid:\/\/shopify\/ProductVariant\/\d+$/.test(normalized)) return normalized;
      return '';
    }

    updateLink(variantId) {
      const normalizedId = this.normalizeVariantGid(variantId).match(/(\d+)$/)?.[1];
      if (!normalizedId) return;

      const previewUrl = new URL(this.link.href, window.location.origin);
      previewUrl.searchParams.set('backdrop_variant', normalizedId);
      this.link.href = `${previewUrl.pathname}${previewUrl.search}`;
    }

    getSourceImage(variant, cachedVariant, product) {
      const sourceImage = variant?.featured_media?.preview_image || variant?.featured_image || variant?.image;
      const sourceUrl = typeof sourceImage === 'string' ? sourceImage : sourceImage?.src || sourceImage?.url;
      const sourceAlt = typeof sourceImage === 'object' ? sourceImage.alt || sourceImage.altText : '';
      const fallbackImage = cachedVariant?.image || product.images?.[0] || null;
      if (!sourceUrl) return fallbackImage;

      return {
        url: sourceUrl,
        alt: sourceAlt || product.title || variant?.title || '',
        width: sourceImage?.width || null,
        height: sourceImage?.height || null,
      };
    }

    preloadBackdrop(variantId, sourceVariant = null) {
      const normalizedId = this.normalizeVariantGid(variantId || sourceVariant?.id);
      const numericId = normalizedId.match(/(\d+)$/)?.[1];
      if (!numericId) return;

      try {
        const product = JSON.parse(this.productData.textContent);
        const handle = String(product.handle || '').trim().toLowerCase();
        if (!handle || !Array.isArray(product.images) || !Array.isArray(product.variants)) return;

        product.handle = handle;
        const cachedVariant = product.variants.find(
          (variant) => this.normalizeVariantGid(variant.id) === normalizedId
        ) || {};
        const sourcePrice = Number(sourceVariant?.price);
        const selectedVariant = {
          ...cachedVariant,
          id: normalizedId,
          sku: sourceVariant?.sku || cachedVariant.sku || '',
          title: sourceVariant?.title || cachedVariant.title || '',
          price: cachedVariant.price ?? (Number.isFinite(sourcePrice) ? sourcePrice / 100 : 0),
          currency: cachedVariant.currency || window.Shopify?.currency?.active || 'USD',
          available: sourceVariant?.available ?? cachedVariant.available ?? false,
          quantityAvailable: cachedVariant.quantityAvailable ?? null,
          image: this.getSourceImage(sourceVariant, cachedVariant, product),
        };
        const timestamp = Date.now();

        sessionStorage.setItem(`${PRODUCT_CACHE_PREFIX}${handle}`, JSON.stringify({ timestamp, product }));
        sessionStorage.setItem(`${VARIANT_CACHE_PREFIX}${numericId}`, JSON.stringify({
          timestamp,
          handle,
          selectedVariant,
        }));

        const imageUrl = selectedVariant.image?.url || product.images[0]?.url;
        if (imageUrl) {
          const preloadImage = new Image();
          preloadImage.src = imageUrl;
        }
      } catch (error) {
        console.warn('Unable to preload the backdrop for 3D Preview:', error);
      }
    }
  }

  customElements.define('backdrop-3d-preview-link', Backdrop3DPreviewLink);
})();
